import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Modal, alpha } from '@mui/material';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import {
  AttachmentResponse,
  CampaignMonitorPicturesResponse,
  FilePointerWithMeta,
  PostgresFilePointer,
  S3FilePointer,
} from '@/api/data-contracts';
import { mediaPlanKeys } from '@/features/media-plans';
import { orderKeys } from '@/features/orders';
import { useTaskImageUpload, useTasks } from '@/features/service-tasks';
import { queryKeys } from '@/react-query/constants';
import useArtworkService from '@/shared/api/artwork.service';
import useUploadedFilesStore from '@/shared/api/uploadedFiles';
import { filesTooLargeMessage } from '@/shared/lib/files';
import { useFileDropTarget } from '@/shared/lib/hooks/useFileDropTarget';
import { useImagePanZoom } from '@/shared/lib/hooks/useImagePanZoom';
import {
  ImageResource,
  bookingArtworkResource,
  usePrefetchResource,
} from '@/shared/lib/hooks/useResourceImage';
import DeleteConfirmationModal from '@/shared/ui/DeleteConfirmationModal';
import { AttachmentOperationGroup } from '@/shared/ui/ImagesModalVersioning';

import ImagePreview from './ImagePreview';
import ImageSidebar from './ImageSidebar';
import { ModalContainer } from './ImageSlider.styles';

const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

type Tab = 'Decoration' | 'Neutralization' | 'Artwork' | 'ArtworkPreview';

type FileWithMeta = PostgresFilePointer | S3FilePointer | FilePointerWithMeta;

const extractFilePointer = (file: FileWithMeta): PostgresFilePointer | S3FilePointer => {
  if ('file' in file) {
    return file.file;
  }
  return file;
};

const extractFileMetadata = (
  file: FileWithMeta,
): { uploadedAt?: string; uploadedBy?: Record<string, string> } => {
  if ('file' in file && file.uploadedBy) {
    return {
      uploadedAt: file.uploadedAt,
      uploadedBy: file.uploadedBy,
    };
  }
  return {};
};

export interface RequiredPhotoDeleteGuard {
  active: boolean;
  message: string;
}

export type MonitorUploadSlot = 'before' | 'after';

/**
 * Fired after every monitor upload, not only the first — callers own the
 * "photos changed" reaction (refreshing their tiles, raising the completion
 * prompt), so they must be told which slot received the photo and be handed the
 * response that produced it.
 */
export type MonitorUpdateHandler = (
  decorationFiles: FileWithMeta[],
  neutralizationFiles: FileWithMeta[],
  slot: MonitorUploadSlot,
  response: CampaignMonitorPicturesResponse,
) => void;

interface Props {
  open: boolean;
  onClose: () => void;
  taskId: string;

  beforeDecorationFiles?: (PostgresFilePointer | S3FilePointer | FilePointerWithMeta)[];
  afterDecorationFiles?: (PostgresFilePointer | S3FilePointer | FilePointerWithMeta)[];
  decorationFiles?: (PostgresFilePointer | S3FilePointer | FilePointerWithMeta)[];
  beforeNeutralizationFiles?: (PostgresFilePointer | S3FilePointer | FilePointerWithMeta)[];
  afterNeutralizationFiles?: (PostgresFilePointer | S3FilePointer | FilePointerWithMeta)[];
  neutralizationFiles?: (PostgresFilePointer | S3FilePointer | FilePointerWithMeta)[];

  artwork?: AttachmentResponse[];
  artworkPreview?: AttachmentResponse[];

  initialTab: Tab;
  initialSelectedObjectId?: string | null;
  autoExpandInitialSelection?: boolean;
  onUpdate?: MonitorUpdateHandler;
  splitMonitorMode?: boolean;

  mode: 'monitor' | 'artwork' | 'orders' | 'mediaplanbookings';

  orderId?: string;
  planId?: string;
  bookingId?: string;
  onArtworkUpdate?: (
    taskId: string,
    type: 'artWork' | 'artWorkPreview',
    files: AttachmentResponse[],
  ) => void;
  hasUploadOption?: boolean;
  hasDeleteOption?: boolean;

  onDownload?: (objectId: string) => Promise<Blob>;
  onUpload?: (files: File[], formKey: string) => Promise<any>;
  onDelete?: (objectId: string) => Promise<any>;
  requiredPhotoDeleteGuard?: RequiredPhotoDeleteGuard;
  hideTabs?: boolean;
  buildResource?: (objectId: string, category: string) => ImageResource | null;
  operationGroups?: AttachmentOperationGroup[];
  initialExpandedOperationId?: string;
}

const ImagesModal: FC<Props> = ({
  open,
  onClose,
  taskId,
  beforeDecorationFiles = [],
  afterDecorationFiles = [],
  decorationFiles = [],
  beforeNeutralizationFiles = [],
  afterNeutralizationFiles = [],
  neutralizationFiles = [],
  artwork = [],
  artworkPreview = [],
  initialTab,
  initialSelectedObjectId = null,
  autoExpandInitialSelection = false,
  onUpdate,
  splitMonitorMode = false,
  mode,
  orderId,
  planId,
  bookingId,
  onArtworkUpdate,
  hasUploadOption = true,
  hasDeleteOption = true,
  onDownload,
  onUpload,
  onDelete,
  requiredPhotoDeleteGuard,
  hideTabs = false,
  buildResource: buildResourceOverride,
  operationGroups,
  initialExpandedOperationId,
}) => {
  const currentModule = window.location.host.split('.')[0];

  const { deleteCampaignMonitorImage } = useTasks();
  const {
    deleteTaskArtwork,
    deleteTaskArtworkPreview,
    uploadTaskArtwork,
    uploadTaskArtworkPreview,
    uploadArtworkForOrder,
    uploadArtworkForMediaPlan,
    uploadArtworkPreviewForOrder,
    uploadArtworkPreviewForMediaPlan,
    deleteArtworkDocumentForOrder,
    deleteArtworkPreviewDocumentForOrder,
    deleteArtworkPreviewDocumentForMediaPlan,
    deleteArtworkDocumentForMediaPlan,
  } = useArtworkService();

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const prefetch = usePrefetchResource();

  const isOrdersMode = mode === 'orders' || mode === 'mediaplanbookings';

  const tabs = useMemo(() => {
    return mode === 'monitor'
      ? (['Decoration', 'Neutralization'] as const)
      : (['Artwork', 'ArtworkPreview'] as const);
  }, [mode]);

  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const [selectedFileId, setSelectedFileId] = useState<number | null>(null);
  const [expandedFileIds, setExpandedFileIds] = useState<Set<number>>(new Set());

  const [rotation, setRotation] = useState(0);
  const { zoom, offsetX, offsetY, zoomIn, zoomOut, imageHandlers } =
    useImagePanZoom(selectedFileId);

  const [localDecorationFiles, setLocalDecorationFiles] = useState<FileWithMeta[]>([]);
  const [localNeutralizationFiles, setLocalNeutralizationFiles] = useState<FileWithMeta[]>([]);
  const [localBeforeDecorationFiles, setLocalBeforeDecorationFiles] = useState<FileWithMeta[]>([]);
  const [localAfterDecorationFiles, setLocalAfterDecorationFiles] = useState<FileWithMeta[]>([]);
  const [localBeforeNeutralizationFiles, setLocalBeforeNeutralizationFiles] = useState<
    FileWithMeta[]
  >([]);
  const [localAfterNeutralizationFiles, setLocalAfterNeutralizationFiles] = useState<
    FileWithMeta[]
  >([]);
  const [localArtwork, setLocalArtwork] = useState<AttachmentResponse[]>([]);
  const [localArtworkPreview, setLocalArtworkPreview] = useState<AttachmentResponse[]>([]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [fileToDeleteId, setFileToDeleteId] = useState<number | null>(null);
  const [deletingObjectId, setDeletingObjectId] = useState<string | null>(null);
  const [uploadSlot, setUploadSlot] = useState<'before' | 'after' | null>(null);
  const [pendingSelectedObjectId, setPendingSelectedObjectId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingUploadOperationIdRef = useRef<string | null>(null);

  const [objectUrls, setObjectUrls] = useState<Record<string, string>>({});
  const objectUrlsRef = useRef<Record<string, string>>({});
  const requestedIds = useRef<Set<string>>(new Set());
  const initialSelectionAppliedRef = useRef(false);
  const uploadSlotRef = useRef<'before' | 'after' | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    objectUrlsRef.current = objectUrls;
  }, [objectUrls]);

  const isSplitMonitorMode = mode === 'monitor' && splitMonitorMode;

  const syncStateKey = useMemo(
    () =>
      JSON.stringify({
        beforeDecorationFiles: beforeDecorationFiles.map((file) => {
          const fp = extractFilePointer(file);
          return fp.objectId ?? fp.name ?? '';
        }),
        afterDecorationFiles: afterDecorationFiles.map((file) => {
          const fp = extractFilePointer(file);
          return fp.objectId ?? fp.name ?? '';
        }),
        beforeNeutralizationFiles: beforeNeutralizationFiles.map((file) => {
          const fp = extractFilePointer(file);
          return fp.objectId ?? fp.name ?? '';
        }),
        afterNeutralizationFiles: afterNeutralizationFiles.map((file) => {
          const fp = extractFilePointer(file);
          return fp.objectId ?? fp.name ?? '';
        }),
        decorationFiles: decorationFiles.map((file) => {
          const fp = extractFilePointer(file);
          return fp.objectId ?? fp.name ?? '';
        }),
        neutralizationFiles: neutralizationFiles.map((file) => {
          const fp = extractFilePointer(file);
          return fp.objectId ?? fp.name ?? '';
        }),
        artwork: artwork.map((file) => file.id ?? file.filePointer?.objectId ?? file.name ?? ''),
        artworkPreview: artworkPreview.map(
          (file) => file.id ?? file.filePointer?.objectId ?? file.name ?? '',
        ),
      }),
    [
      afterDecorationFiles,
      afterNeutralizationFiles,
      artwork,
      artworkPreview,
      beforeDecorationFiles,
      beforeNeutralizationFiles,
      decorationFiles,
      neutralizationFiles,
    ],
  );

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    setLocalDecorationFiles(decorationFiles || []);
    setLocalNeutralizationFiles(neutralizationFiles || []);
    setLocalBeforeDecorationFiles(beforeDecorationFiles || []);
    setLocalAfterDecorationFiles(afterDecorationFiles || []);
    setLocalBeforeNeutralizationFiles(beforeNeutralizationFiles || []);
    setLocalAfterNeutralizationFiles(afterNeutralizationFiles || []);
    setLocalArtwork(artwork ?? []);
    setLocalArtworkPreview(artworkPreview ?? []);
    wasOpenRef.current = true;
    // syncStateKey is a content hash of the file props. Re-sync whenever the
    // underlying data actually changes (e.g. fresh data lands after a refetch),
    // so the modal can't stay frozen on a stale snapshot it captured at open.
    // In-modal optimistic edits update local state before props change, so
    // syncStateKey is unchanged and they are not clobbered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, syncStateKey]);

  useEffect(() => {
    if (!open) return;
    const allowed = tabs as readonly Tab[];
    setActiveTab(allowed.includes(initialTab) ? initialTab : allowed[0]);
    setErrorMessage(null);
    setExpandedFileIds(new Set());
    setPendingSelectedObjectId(null);
    initialSelectionAppliedRef.current = false;
  }, [open, initialTab, tabs]);

  useEffect(() => {
    setRotation(0);
  }, [selectedFileId]);

  const updateLocalLists = useCallback(
    (response: CampaignMonitorPicturesResponse | undefined) => {
      if (!response) return;

      const newDeco = [
        ...(response.beforeDecorationFiles || []),
        ...(response.afterDecorationFiles || []),
      ];
      const newNeutral = [
        ...(response.beforeNeutralizationFiles || []),
        ...(response.afterNeutralizationFiles || []),
      ];

      setLocalDecorationFiles(newDeco);
      setLocalNeutralizationFiles(newNeutral);
      setLocalBeforeDecorationFiles(response.beforeDecorationFiles || []);
      setLocalAfterDecorationFiles(response.afterDecorationFiles || []);
      setLocalBeforeNeutralizationFiles(response.beforeNeutralizationFiles || []);
      setLocalAfterNeutralizationFiles(response.afterNeutralizationFiles || []);

      onUpdate?.(newDeco, newNeutral, uploadSlotRef.current ?? 'after', response);
    },
    [onUpdate],
  );

  const staticFiles = useMemo(() => {
    const processFiles = (files: FileWithMeta[], category: 'Decoration' | 'Neutralization') => {
      if (!files || !Array.isArray(files)) return [];

      return files.map((f, index) => {
        const fp = extractFilePointer(f);
        const meta = extractFileMetadata(f);

        const isBeforeSlot =
          category === 'Decoration'
            ? localBeforeDecorationFiles.some((item) => {
                const itemFp = extractFilePointer(item);
                return itemFp.objectId === fp.objectId;
              })
            : localBeforeNeutralizationFiles.some((item) => {
                const itemFp = extractFilePointer(item);
                return itemFp.objectId === fp.objectId;
              });
        const idBase =
          category === 'Decoration' ? (isBeforeSlot ? 10000 : 20000) : isBeforeSlot ? 30000 : 40000;
        const numericId = fp.objectId ? parseInt(fp.objectId, 10) : index;
        const safeId = (isNaN(numericId) ? index : numericId) + idBase;

        const uploaderName = meta.uploadedBy?.userName || 'System';
        const uploaderInitial = uploaderName.charAt(0).toUpperCase();
        const uploadedDate = meta.uploadedAt
          ? dayjs(meta.uploadedAt).format('D MMM, YYYY - HH:mm')
          : dayjs().format('D MMM, YYYY - HH:mm');
        const displayDate = meta.uploadedAt
          ? dayjs(meta.uploadedAt).format('D MMM, YYYY')
          : dayjs().format('D MMM, YYYY');

        const uploadedByModule = meta.uploadedBy?.module;
        const moduleCanDelete = (() => {
          if (!hasDeleteOption) return false;
          if (uploadedByModule) return uploadedByModule === currentModule;
          if (currentModule === 'services' || currentModule === 'location') {
            return (isBeforeSlot ? 'location' : 'services') === currentModule;
          }
          return true;
        })();

        const isRequiredSlot = category === 'Decoration' ? !isBeforeSlot : isBeforeSlot;
        const requiredSlotFiles =
          category === 'Decoration' ? localAfterDecorationFiles : localBeforeNeutralizationFiles;
        const deleteBlockedReason =
          requiredPhotoDeleteGuard?.active && isRequiredSlot && requiredSlotFiles.length <= 1
            ? requiredPhotoDeleteGuard.message
            : undefined;

        return {
          id: safeId,
          category,
          slot: isBeforeSlot ? 'before' : 'after',
          title: fp.name || 'Untitled',
          displayDate,
          realFileName: fp.name || 'unknown',
          fileType: fp.name?.split('.').pop()?.toUpperCase() || 'FILE',
          uploadDate: uploadedDate,
          uploaderName,
          uploaderInitial,
          objectId: fp.objectId,
          thumbnail: '',
          canDelete: moduleCanDelete,
          deleteBlockedReason,
        };
      });
    };

    const processAttachments = (
      files: AttachmentResponse[],
      category: 'Artwork' | 'ArtworkPreview',
    ) => {
      return files.map((f, index) => {
        const activeVersion = f.versions?.find((v) => v.id === f.activeVersion);
        let uploaderName = 'System';
        let uploaderInitial = 'S';

        if (activeVersion?.uploadedBy) {
          try {
            const uploadedByData =
              typeof activeVersion.uploadedBy === 'string'
                ? JSON.parse(activeVersion.uploadedBy)
                : activeVersion.uploadedBy;
            uploaderName = uploadedByData.userName || uploadedByData.name || 'System';
            uploaderInitial = uploaderName.charAt(0).toUpperCase();
          } catch {
            uploaderName = 'System';
            uploaderInitial = 'S';
          }
        }

        const uploadedDate = activeVersion?.uploadDate
          ? dayjs(activeVersion.uploadDate).format('D MMM, YYYY - HH:mm')
          : dayjs().format('D MMM, YYYY - HH:mm');
        const displayDate = activeVersion?.uploadDate
          ? dayjs(activeVersion.uploadDate).format('D MMM, YYYY')
          : dayjs().format('D MMM, YYYY');

        return {
          id: index + (category === 'Artwork' ? 30000 : 40000),
          attachmentId: f.id,
          category,
          title: f.name || 'Untitled',
          displayDate,
          realFileName: f.filePointer?.name || 'unknown',
          fileType: f.filePointer?.name?.split('.').pop()?.toUpperCase() || 'FILE',
          uploadDate: uploadedDate,
          uploaderName,
          uploaderInitial,
          objectId: f.filePointer?.objectId,
          thumbnail: '',
          canDelete: hasDeleteOption,
        };
      });
    };

    if (mode === 'monitor') {
      return [
        ...processFiles(localDecorationFiles, 'Decoration'),
        ...processFiles(localNeutralizationFiles, 'Neutralization'),
      ];
    }

    return [
      ...processAttachments(localArtwork ?? [], 'Artwork'),
      ...processAttachments(localArtworkPreview ?? [], 'ArtworkPreview'),
    ];
  }, [
    mode,
    localBeforeDecorationFiles,
    localAfterDecorationFiles,
    localBeforeNeutralizationFiles,
    localDecorationFiles,
    localNeutralizationFiles,
    localArtwork,
    localArtworkPreview,
    currentModule,
    hasDeleteOption,
    requiredPhotoDeleteGuard,
  ]);

  const allFiles = useMemo(() => {
    return staticFiles.map((file) => ({
      ...file,
      thumbnail:
        file.objectId && objectUrls[String(file.objectId)]
          ? objectUrls[String(file.objectId)]
          : file.thumbnail,
    }));
  }, [staticFiles, objectUrls]);

  const buildResource = useCallback(
    (objectId: string, category: string): ImageResource | null => {
      if (buildResourceOverride) return buildResourceOverride(objectId, category);
      if (isOrdersMode) {
        return bookingArtworkResource({ orderId, planId, bookingId: bookingId! }, objectId, {
          preview: category === 'ArtworkPreview',
        });
      }
      if (category === 'Artwork')
        return { kind: 'service-task-artwork', taskId, documentId: objectId };
      if (category === 'ArtworkPreview')
        return { kind: 'service-task-artwork-preview', taskId, documentId: objectId };
      if (onDownload) return null;
      return { kind: 'service-task-campaign-monitor', taskId, documentId: objectId };
    },
    [buildResourceOverride, taskId, isOrdersMode, orderId, planId, bookingId, onDownload],
  );

  const fetchImage = useCallback(
    async (objectId: string, category: string) => {
      if (requestedIds.current.has(objectId)) return;
      requestedIds.current.add(objectId);

      try {
        const resource = buildResource(objectId, category);
        const blob = resource ? await prefetch(resource) : await onDownload!(objectId);

        if (blob && blob.size > 0) {
          const url = URL.createObjectURL(blob);
          setObjectUrls((prev) => ({ ...prev, [objectId]: url }));
        }
      } catch (error) {
        requestedIds.current.delete(objectId);
        console.error('Failed to load image', objectId, error);
      }
    },
    [prefetch, buildResource, onDownload],
  );

  useEffect(() => {
    if (!open) return;
    const filesToLoad = allFiles.filter((f) => f.category === activeTab);
    filesToLoad.forEach((f) => {
      const id = String(f.objectId);
      if (f.objectId && !objectUrls[id] && !requestedIds.current.has(id)) {
        fetchImage(id, f.category);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, activeTab, allFiles, fetchImage]);

  useEffect(() => {
    return () => {
      Object.values(objectUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const filteredFiles = useMemo(
    () =>
      allFiles.filter(
        (file) => file.category === activeTab && String(file.objectId ?? '') !== deletingObjectId,
      ),
    [allFiles, activeTab, deletingObjectId],
  );

  const operationSections = useMemo(() => {
    if (!operationGroups?.length || mode !== 'orders' || activeTab !== 'Artwork') return undefined;
    const byAttachmentId = new Map<string, typeof filteredFiles>();
    filteredFiles.forEach((file) => {
      if (!('attachmentId' in file) || !file.attachmentId) return;
      const list = byAttachmentId.get(file.attachmentId) ?? [];
      list.push(file);
      byAttachmentId.set(file.attachmentId, list);
    });
    const claimed = new Set<string>();
    const sections = operationGroups.map((group) => ({
      group,
      files: group.attachmentIds.flatMap((id) => {
        claimed.add(id);
        return byAttachmentId.get(id) ?? [];
      }),
    }));
    const leftovers = filteredFiles.filter(
      (file) => 'attachmentId' in file && file.attachmentId && !claimed.has(file.attachmentId),
    );
    if (leftovers.length && sections.length) {
      sections[sections.length - 1].files.push(...leftovers);
    }
    return sections;
  }, [operationGroups, mode, activeTab, filteredFiles]);

  const selectedFile = useMemo(() => {
    return filteredFiles.find((f) => f.id === selectedFileId) || null;
  }, [filteredFiles, selectedFileId]);

  const getMonitorFileId = useCallback(
    (
      category: 'Decoration' | 'Neutralization',
      slot: 'before' | 'after',
      objectId?: string | number,
    ) => {
      const idBase =
        category === 'Decoration'
          ? slot === 'before'
            ? 10000
            : 20000
          : slot === 'before'
            ? 30000
            : 40000;
      const numericId =
        objectId == null
          ? 0
          : typeof objectId === 'number'
            ? objectId
            : parseInt(String(objectId), 10);
      return (Number.isNaN(numericId) ? 0 : numericId) + idBase;
    },
    [],
  );

  useEffect(() => {
    if (!open) {
      setSelectedFileId(null);
      setExpandedFileIds(new Set());
      return;
    }
  }, [open, activeTab]);

  const uploadedFileNames = useUploadedFilesStore((s) => s.files.map((f) => f.name));

  useEffect(() => {
    if (!pendingSelectedObjectId) return;

    const pendingFile = filteredFiles.find(
      (file) => String(file.objectId) === String(pendingSelectedObjectId),
    );

    if (!pendingFile) return;

    setSelectedFileId(pendingFile.id);
    setExpandedFileIds(new Set());
    setPendingSelectedObjectId(null);
  }, [filteredFiles, pendingSelectedObjectId]);

  useEffect(() => {
    if (filteredFiles.length === 0) {
      if (selectedFileId !== null) {
        setSelectedFileId(null);
      }
      if (expandedFileIds.size > 0) {
        setExpandedFileIds(new Set());
      }
      return;
    }

    if (selectedFileId != null && !filteredFiles.some((f) => f.id === selectedFileId)) {
      setSelectedFileId(null);
    }

    const validExpandedIds = Array.from(expandedFileIds).filter((id) =>
      filteredFiles.some((f) => f.id === id),
    );
    if (validExpandedIds.length !== expandedFileIds.size) {
      setExpandedFileIds(new Set(validExpandedIds));
    }
  }, [filteredFiles, selectedFileId, expandedFileIds]);

  useEffect(() => {
    if (!open || filteredFiles.length === 0) return;

    if (selectedFileId !== null && filteredFiles.some((f) => f.id === selectedFileId)) return;

    if (activeTab === 'Decoration' || activeTab === 'Neutralization') {
      const beforeFile = filteredFiles.find((f) => 'slot' in f && f.slot === 'before');
      if (beforeFile) {
        setSelectedFileId(beforeFile.id);
        setExpandedFileIds(new Set());
      } else {
        const afterFile = filteredFiles.find((f) => 'slot' in f && f.slot === 'after');
        if (afterFile) {
          setSelectedFileId(afterFile.id);
          setExpandedFileIds(new Set());
        }
      }
    } else {
      setSelectedFileId(filteredFiles[0].id);
      setExpandedFileIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filteredFiles, open]);

  useEffect(() => {
    if (!open || initialSelectionAppliedRef.current) return;

    if (initialSelectedObjectId) {
      const initialFile = filteredFiles.find(
        (file) => String(file.objectId) === String(initialSelectedObjectId),
      );
      if (initialFile) {
        setSelectedFileId(initialFile.id);
        setExpandedFileIds(autoExpandInitialSelection ? new Set([initialFile.id]) : new Set());
        initialSelectionAppliedRef.current = true;
        return;
      }
      return;
    }

    if (uploadedFileNames.length > 0) {
      const lastUploadedName = uploadedFileNames[uploadedFileNames.length - 1];
      const matchByName = filteredFiles.find((file) => file.realFileName === lastUploadedName);
      if (matchByName) {
        setSelectedFileId(matchByName.id);
        setExpandedFileIds(new Set());
        initialSelectionAppliedRef.current = true;
        return;
      }
    }

    setSelectedFileId(null);
    initialSelectionAppliedRef.current = true;
  }, [
    open,
    activeTab,
    filteredFiles,
    initialSelectedObjectId,
    autoExpandInitialSelection,
    uploadedFileNames,
  ]);

  const handleCardClick = (id: number) => {
    if (selectedFileId !== id) {
      setSelectedFileId(id);
      setExpandedFileIds((prev) => new Set([...prev, id]));
      return;
    }

    setExpandedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handlePrev = () => {
    if (!selectedFile || filteredFiles.length === 0) return;
    const currentIndex = filteredFiles.findIndex((f) => f.id === selectedFile.id);
    const prevIndex = (currentIndex - 1 + filteredFiles.length) % filteredFiles.length;
    setSelectedFileId(filteredFiles[prevIndex].id);
  };

  const handleNext = () => {
    if (!selectedFile || filteredFiles.length === 0) return;
    const currentIndex = filteredFiles.findIndex((f) => f.id === selectedFile.id);
    const nextIndex = (currentIndex + 1) % filteredFiles.length;
    setSelectedFileId(filteredFiles[nextIndex].id);
  };

  const uploadMutation = useTaskImageUpload({
    taskId,
    onSuccess: (response) => {
      updateLocalLists(response);
    },
    onError: (error: any) => {
      if (error?.response?.data?.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage('Upload failed.');
      }
    },
  });

  const removeMonitorFileLocally = useCallback(
    (deletedId: string, category: string, slot?: 'before' | 'after') => {
      if (category === 'Decoration') {
        if (slot === 'before') {
          setLocalBeforeDecorationFiles((prev) =>
            prev.filter((file) => String(extractFilePointer(file).objectId) !== deletedId),
          );
        } else if (slot === 'after') {
          setLocalAfterDecorationFiles((prev) =>
            prev.filter((file) => String(extractFilePointer(file).objectId) !== deletedId),
          );
        }

        setLocalDecorationFiles((prev) =>
          prev.filter((file) => String(extractFilePointer(file).objectId) !== deletedId),
        );
        return;
      }

      if (category === 'Neutralization') {
        if (slot === 'before') {
          setLocalBeforeNeutralizationFiles((prev) =>
            prev.filter((file) => String(extractFilePointer(file).objectId) !== deletedId),
          );
        } else if (slot === 'after') {
          setLocalAfterNeutralizationFiles((prev) =>
            prev.filter((file) => String(extractFilePointer(file).objectId) !== deletedId),
          );
        }

        setLocalNeutralizationFiles((prev) =>
          prev.filter((file) => String(extractFilePointer(file).objectId) !== deletedId),
        );
      }
    },
    [],
  );

  const handleDeleteSuccess = useCallback(
    (
      response: any,
      variables: { objectId: string | number; category: string; slot?: 'before' | 'after' },
    ) => {
      const category = variables.category;
      const deletedId = String(variables.objectId);
      const slot = variables.slot;

      if (isOrdersMode) {
        if (category === 'ArtworkPreview') {
          setLocalArtworkPreview((prev) =>
            prev.filter((a) => String(a.filePointer?.objectId) !== deletedId),
          );
        } else {
          setLocalArtwork((prev) =>
            prev.filter((a) => String(a.filePointer?.objectId) !== deletedId),
          );
        }
        if (orderId) queryClient.invalidateQueries(orderKeys.detail(orderId));
        if (planId) queryClient.invalidateQueries(mediaPlanKeys.detail(planId));
      } else if (category === 'Artwork') {
        setLocalArtwork((prev) => {
          const updated = prev.filter((a) => String(a.filePointer?.objectId) !== deletedId);
          onArtworkUpdate?.(taskId, 'artWork', updated);
          return updated;
        });
      } else if (category === 'ArtworkPreview') {
        setLocalArtworkPreview((prev) => {
          const updated = prev.filter((a) => String(a.id) !== deletedId);
          onArtworkUpdate?.(taskId, 'artWorkPreview', updated);
          return updated;
        });
      } else if (category === 'Decoration' || category === 'Neutralization') {
        removeMonitorFileLocally(deletedId, category, slot);
        queryClient.invalidateQueries({ queryKey: [queryKeys.journeyProgress, taskId] });
      }

      setObjectUrls((prev) => {
        const existingUrl = prev[deletedId];
        if (existingUrl) URL.revokeObjectURL(existingUrl);

        const rest = { ...prev };
        delete rest[deletedId];
        return rest;
      });
      requestedIds.current.delete(deletedId);

      if (!isOrdersMode) {
        queryClient.invalidateQueries({ queryKey: [queryKeys.serviceOrders] });
      }

      setDeleteModalOpen(false);
      setFileToDeleteId(null);
      setDeletingObjectId(null);
    },
    [isOrdersMode, onArtworkUpdate, orderId, planId, queryClient, removeMonitorFileLocally, taskId],
  );

  const deleteMutation = useMutation({
    mutationFn: async ({
      objectId,
      category,
      attachmentId,
    }: {
      objectId: string | number;
      category: string;
      attachmentId?: string;
      slot?: 'before' | 'after';
    }) => {
      const id = String(objectId);

      if (isOrdersMode) {
        const bId = bookingId!;
        if (category === 'ArtworkPreview') {
          if (orderId) return await deleteArtworkPreviewDocumentForOrder(orderId, bId, id);
          if (planId) return await deleteArtworkPreviewDocumentForMediaPlan(planId, bId, id);
          return null;
        } else {
          if (orderId) return await deleteArtworkDocumentForOrder(orderId, bId, id);
          if (planId) return await deleteArtworkDocumentForMediaPlan(planId, bId, id);
          return null;
        }
      }

      if (category === 'Artwork') return await deleteTaskArtwork(taskId, attachmentId || id);
      if (category === 'ArtworkPreview') return await deleteTaskArtworkPreview(taskId, id);
      if (onDelete) return await onDelete(id);
      return await deleteCampaignMonitorImage(taskId, id);
    },

    onSuccess: (response, variables) => {
      handleDeleteSuccess(response, variables);
    },

    onError: (error: any, variables) => {
      const status = error?.response?.status;
      if (status === 404) {
        handleDeleteSuccess(null, variables);
        return;
      }

      setErrorMessage(error?.response?.data?.message ?? 'Delete failed.');
      setDeleteModalOpen(false);
      setDeletingObjectId(null);
    },
  });

  const updateLocalArtworkFromBooking = useCallback(
    (response: any, bId: string, isPreview: boolean) => {
      const allBookings = [
        ...((response?.bookings as any[]) ?? []),
        ...((response?.packageBookings as any[]) ?? []).flatMap((p: any) => p.bookings ?? []),
      ];
      const updatedBooking = allBookings.find((b: any) => b.id === bId);
      if (!updatedBooking) return;

      if (isPreview) {
        setLocalArtworkPreview(updatedBooking.artworkPreview ?? []);
      } else {
        setLocalArtwork(updatedBooking.artwork ?? []);
      }
    },
    [],
  );

  const handleUploadClick = () => {
    pendingUploadOperationIdRef.current = null;
    fileInputRef.current?.click();
  };

  const { isDragActive, dropTargetProps } = useFileDropTarget({
    onFiles: (files) => {
      pendingUploadOperationIdRef.current = null;
      void uploadPickedFiles(files);
    },
    disabled: !hasUploadOption || isSplitMonitorMode,
    accept: 'image/*,application/pdf,image/tiff,video/mp4',
    onRejected: () =>
      setErrorMessage(
        t('errors.file_invalid_type', { types: 'image/*, application/pdf, video/mp4' }),
      ),
  });

  const handleUploadToOperation = (operationId?: string) => {
    pendingUploadOperationIdRef.current = operationId ?? null;
    fileInputRef.current?.click();
  };

  const handleUploadSlotClick = (slot: 'before' | 'after') => {
    setUploadSlot(slot);
    uploadSlotRef.current = slot;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    const files = Array.from(event.target.files);
    event.target.value = '';
    await uploadPickedFiles(files);
  };

  const uploadPickedFiles = async (files: File[]) => {
    if (files.length === 0) return;
    setErrorMessage(null);

    const invalid = files.filter((f) => f.size > MAX_FILE_SIZE);
    const valid = files.filter((f) => f.size <= MAX_FILE_SIZE);

    if (invalid.length) {
      setErrorMessage(
        filesTooLargeMessage(
          t,
          invalid.map((f) => f.name),
          MAX_FILE_SIZE,
        ),
      );
    }

    if (!valid.length) return;

    if (isOrdersMode) {
      const bId = bookingId!;
      const isPreview = activeTab === 'ArtworkPreview';
      const pendingOperationId = pendingUploadOperationIdRef.current ?? undefined;
      pendingUploadOperationIdRef.current = null;

      if (orderId) {
        const response = isPreview
          ? await uploadArtworkPreviewForOrder(orderId, bId, valid, pendingOperationId)
          : await uploadArtworkForOrder(orderId, bId, valid, pendingOperationId);
        updateLocalArtworkFromBooking(response, bId, isPreview);
        await queryClient.invalidateQueries(orderKeys.detail(orderId));
        if (pendingOperationId) {
          // useBookingOperations' key: bookingsApi shares the orders root, and importing it here
          // would make media -> bookings close an orders <-> bookings import cycle.
          await queryClient.invalidateQueries(orderKeys.sub(orderId, 'operations', bId));
        }
      } else if (planId) {
        const response = isPreview
          ? await uploadArtworkPreviewForMediaPlan(planId, bId, valid)
          : await uploadArtworkForMediaPlan(planId, bId, valid);
        updateLocalArtworkFromBooking(response, bId, isPreview);
        await queryClient.invalidateQueries(mediaPlanKeys.detail(planId));
      }
      return;
    }

    if (mode === 'artwork') {
      if (activeTab === 'Artwork') {
        const response = await uploadTaskArtwork(taskId, valid);
        setLocalArtwork(response.artWork ?? []);
        onArtworkUpdate?.(taskId, 'artWork', response.artWork ?? []);
      } else {
        const response = await uploadTaskArtworkPreview(taskId, valid);
        setLocalArtworkPreview(response.artWorkPreview ?? []);
        onArtworkUpdate?.(taskId, 'artWorkPreview', response.artWorkPreview ?? []);
      }
      await queryClient.invalidateQueries({ queryKey: [queryKeys.serviceOrders] });
      return;
    }

    // mode === 'monitor'
    if (isSplitMonitorMode) {
      const currentSlot = uploadSlotRef.current ?? uploadSlot ?? 'after';
      const existingFile =
        activeTab === 'Decoration'
          ? currentSlot === 'before'
            ? localBeforeDecorationFiles[0]
            : localAfterDecorationFiles[0]
          : currentSlot === 'before'
            ? localBeforeNeutralizationFiles[0]
            : localAfterNeutralizationFiles[0];

      if (existingFile?.objectId) {
        try {
          if (onDelete) {
            await onDelete(existingFile.objectId);
          } else {
            await deleteCampaignMonitorImage(taskId, existingFile.objectId);
          }
        } catch (error: any) {
          if (error?.response?.status !== 404) {
            throw error;
          }
        }
      }

      const formKey =
        activeTab === 'Decoration'
          ? currentSlot === 'before'
            ? 'beforeDecorationFiles'
            : 'afterDecorationFiles'
          : currentSlot === 'before'
            ? 'beforeNeutralizationFiles'
            : 'afterNeutralizationFiles';

      try {
        const response = onUpload
          ? await onUpload(valid.slice(0, 1), formKey)
          : await uploadMutation.mutateAsync({ files: valid.slice(0, 1), formKey });
        updateLocalLists(response);
        const uploadedFile =
          activeTab === 'Decoration'
            ? currentSlot === 'before'
              ? response.beforeDecorationFiles?.[0]
              : response.afterDecorationFiles?.[0]
            : currentSlot === 'before'
              ? response.beforeNeutralizationFiles?.[0]
              : response.afterNeutralizationFiles?.[0];

        if (uploadedFile?.objectId) {
          await fetchImage(String(uploadedFile.objectId), activeTab);
          setSelectedFileId(getMonitorFileId(activeTab, currentSlot, uploadedFile.objectId));
          setExpandedFileIds(new Set());
          setPendingSelectedObjectId(String(uploadedFile.objectId));
        } else {
          setPendingSelectedObjectId(null);
        }
        await queryClient.invalidateQueries([queryKeys.serviceOrders]);
      } catch (error: any) {
        if (onUpload) {
          setErrorMessage(error?.response?.data?.message ?? 'Upload failed.');
        }
      } finally {
        setUploadSlot(null);
        uploadSlotRef.current = null;
      }
      return;
    }

    const formKey =
      activeTab === 'Decoration' ? 'afterDecorationFiles' : 'afterNeutralizationFiles';
    uploadMutation.mutate({ files: valid, formKey });
  };

  const handleRequestDelete = (id: number) => {
    setFileToDeleteId(id);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setFileToDeleteId(null);
  };

  const handleConfirmDelete = () => {
    if (!fileToDeleteId) return;

    const fileToDelete = allFiles.find((f) => f.id === fileToDeleteId);
    if (!fileToDelete || !fileToDelete.objectId) {
      setDeleteModalOpen(false);
      return;
    }

    setDeletingObjectId(String(fileToDelete.objectId));

    deleteMutation.mutate(
      {
        objectId: String(fileToDelete.objectId),
        category: fileToDelete.category,
        ...(fileToDelete.category === 'Artwork' &&
        'attachmentId' in fileToDelete &&
        fileToDelete.attachmentId
          ? { attachmentId: fileToDelete.attachmentId }
          : {}),
        slot: fileToDelete.slot,
      },
      {
        onSuccess: () => {
          if (selectedFileId === fileToDeleteId) {
            const currentIndex = filteredFiles.findIndex((f) => f.id === fileToDeleteId);
            if (filteredFiles.length > 1) {
              const nextIndex =
                currentIndex === filteredFiles.length - 1 ? currentIndex - 1 : currentIndex + 1;
              if (filteredFiles[nextIndex]) setSelectedFileId(filteredFiles[nextIndex].id);
            } else {
              setSelectedFileId(null);
            }
          }
        },
      },
    );
  };

  const handleRotate = () => setRotation((prev) => prev + 90);

  const handleDownload = () => {
    if (selectedFile && selectedFile.objectId && objectUrls[String(selectedFile.objectId)]) {
      const link = document.createElement('a');
      link.href = objectUrls[String(selectedFile.objectId)];
      link.download = selectedFile.realFileName || 'download.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExpand = () => {
    if (selectedFile && selectedFile.objectId && objectUrls[String(selectedFile.objectId)]) {
      window.open(objectUrls[String(selectedFile.objectId)], '_blank');
    }
  };

  const beforeFiles = filteredFiles.filter((f) => f.slot === 'before');
  const afterFiles = filteredFiles.filter((f) => f.slot === 'after');

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <ModalContainer
          data-testid="images-modal"
          {...dropTargetProps}
          sx={
            isDragActive
              ? {
                  backgroundColor: (theme) => alpha(theme.palette.accent.main, 0.1),
                  outline: (theme) => `2px dashed ${theme.palette.accent.main}`,
                  outlineOffset: '-2px',
                }
              : undefined
          }
        >
          <ImagePreview
            selectedFile={selectedFile}
            rotation={rotation}
            zoom={zoom}
            offsetX={offsetX}
            offsetY={offsetY}
            imageHandlers={imageHandlers}
            hasMultipleFiles={filteredFiles.length > 1}
            hasDeleteOption={selectedFile?.canDelete ?? hasDeleteOption}
            onPrev={handlePrev}
            onNext={handleNext}
            onRotate={handleRotate}
            onZoomIn={zoomIn}
            onZoomOut={zoomOut}
            onDownload={handleDownload}
            onExpand={handleExpand}
            onDelete={() => selectedFile && handleRequestDelete(selectedFile.id)}
          />

          <ImageSidebar
            tabs={tabs}
            activeTab={activeTab}
            files={filteredFiles}
            monitorMode={isSplitMonitorMode}
            beforeFiles={beforeFiles}
            afterFiles={afterFiles}
            selectedFileId={selectedFileId}
            expandedFileIds={expandedFileIds}
            errorMessage={errorMessage}
            fileInputRef={fileInputRef}
            onTabChange={setActiveTab}
            onClose={onClose}
            onUploadClick={handleUploadClick}
            onUploadSlotClick={handleUploadSlotClick}
            onFileChange={handleFileChange}
            onCardClick={handleCardClick}
            onDeleteFile={handleRequestDelete}
            maxFileSizeMb={30}
            hasUploadOption={hasUploadOption}
            hasDeleteOption={hasDeleteOption}
            hideTabs={hideTabs}
            operationSections={operationSections}
            initialExpandedOperationId={initialExpandedOperationId}
            onUploadToOperation={handleUploadToOperation}
          />
        </ModalContainer>
      </Modal>

      <DeleteConfirmationModal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default ImagesModal;
